<style lang="scss">
  @use "../../../../assets/css/global.scss" as *;
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/colors";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/utils";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/mixins";

  $primary-color: colors.get(primary_60);

  .text-input {
    padding: utils.in_rem(24px);
    width: utils.in_rem(400px);
  }

  .footer {
    display: flex;
    padding: 0px 24px 12px 24px;
    justify-content: flex-end;
    align-items: center;
    gap: 17px;
    align-self: stretch;
    flex-direction: row;
  }

  .body {
    display: flex;
    padding: 24px 32px;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 24px;
  }

  .candidate-info-container {
    display: flex;
    align-items: flex-start;
    gap: 16px;
  }

  .candidate-info {
    display: flex;
    align-items: flex-start;
    gap: x;
    flex-direction: column;
    flex-wrap: wrap;
    width: auto;
    max-width: 100%;
    h3 {
      @include colors.color(textBlack);
    }
    .job {
      @include mixins.body2();
      @include colors.color(textLighterBlack);
    }
  }

  .modal-header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: colors.get(primary_50);
    width: calc(100% + 2rem);
    margin: -1rem;
    padding: 1rem 1.5rem;
    box-sizing: border-box;
  }

  .title {
    @include colors.color(textWhite);
  }
</style>

<script>
  // Functions
  import { hasLicense } from "@sourcewhale/shared/src/assets/js/src/general/licenses";
  import { wrappedSpreadsheetMap } from "./utils";
  // Components
  import Avatar from "@sourcewhale/shared/svelte-design-system/components/atoms/Avatar.svelte";
  import Button from "@sourcewhale/shared/svelte-design-system/components/atoms/Button.svelte";
  import CloseButton from "@sourcewhale/shared/svelte-design-system/components/molecules/Modal/CloseButton.svelte";
  import Modal from "@sourcewhale/shared/svelte-design-system/components/molecules/Modal/Modal.svelte";
  import JobInfo from "../../elements/ProfileContactSideBarShared/JobInfo.svelte";
  import BulkEnrichModal from "../../pages/Search/components/AddToSourceWhale/BulkEnrichModal.svelte";
  import CampaignDropdown from "./CampaignDropdown.svelte";
  // Stores
  import { userStore } from "@sourcewhale/shared/src/svelte/api-stores/user-store";
  import { hasModalBeenOpenedStore, selectedCampaignIdStore } from "./stores";

  export let actionedFrom = ""; // "opportunityModal", "searchPage" or empty
  export let callback = () => {}; // e.g. for closing the modal in the parent component
  export let candidates = []; // Can be a single candidate from todos modal
  export let avatarProps = {}; // For a single candidate, used to display their avatar
  export let jobInfo = {}; // For a single candidate, used to display their job info
  export let openInNewTab = false;
  export let showAvatar = true; // Show the avatar and job info section
  export let offerBulkEnrich = false; // Whether to offer bulk enrich option (checks candidates for missing phones/emails)

  let showBulkEnrichModal = false;

  const closeModal = () => {
    showBulkEnrichModal = false;
    selectedCampaignIdStore.set(null);
    callback();
  };

  const confirmInner = async (body) => {
    const params = await wrappedSpreadsheetMap(body);

    if (actionedFrom) params.actionedFrom = actionedFrom;

    closeModal();
    if (openInNewTab) {
      window.open(
        `/review?${new URLSearchParams(params).toString()}`,
        "_blank"
      );
    } else {
      window.location = `/review?${new URLSearchParams(params).toString()}`;
    }
  };

  const confirm = async () => {
    const goThroughBulkEnrich =
      offerBulkEnrich &&
      hasLicense($userStore, "data") &&
      candidates.some(
        (c) => (c.emails || []).length === 0 || (c.phones || []).length === 0
      );
    if (goThroughBulkEnrich) {
      showBulkEnrichModal = true;
      return;
    }

    await confirmInner({ candidates, campaignId: $selectedCampaignIdStore });
  };

  // Set the modal as opened, initiating the open campaigns API call
  hasModalBeenOpenedStore.set(true);
</script>

<Modal closeButton={false} blur="4px" onclose={closeModal}>
  <div slot="header" class="modal-header-content">
    <h2 class="title">Add to a campaign</h2>
    <CloseButton
      onclose={closeModal}
      class="close-button"
      color="interfaceTransparentWhite_70"
      mouseEnterColor="interfaceTransparentWhite_50"
      focusColor="interfaceTransparentWhite_30"
    />
  </div>
  <div class="body">
    <!-- Only show candidate info if a single candidate is being added and the info is present -->
    {#if candidates.length === 1 && (!showAvatar || Object.keys(avatarProps).length) && Object.keys(jobInfo).length}
      <div class="candidate-info-container">
        {#if showAvatar}
          <Avatar {...avatarProps} size="large" />
        {/if}
        <div class="candidate-info">
          <h3>{jobInfo?.candidateName}</h3>
          <div class="job"><JobInfo {...jobInfo} /></div>
        </div>
      </div>
    {/if}
    <CampaignDropdown />
  </div>
  <div class="footer">
    <Button size="large" class="flex-1" onclick={closeModal} kind="ghost-blue"
      >Cancel</Button
    >
    <Button
      size="large"
      class="flex-1{$selectedCampaignIdStore ? '' : ' disabled'}"
      onclick={confirm}
      disabled={!$selectedCampaignIdStore}>Next</Button
    >
  </div>
</Modal>

{#if showBulkEnrichModal}
  <BulkEnrichModal
    {candidates}
    campaignId={$selectedCampaignIdStore}
    confirmCallback={confirmInner}
    onclose={() => {
      showBulkEnrichModal = false;
    }}
  />
{/if}
